// src/firebase.js
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { initializeFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";
import { getStorage } from "firebase/storage";
import { isE2EMockEnabled, subscribeE2EAuth } from "./e2eMock";

const REQUIRED_FIREBASE_FIELDS = [
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
];

const productionFirebaseConfig = {
  apiKey: "AIzaSyCdBC2PK-Mj24a-evKdDcujA0A2arySjec",
  // Keep the Firebase-hosted auth domain for Google OAuth popup flows.
  // The public site is lifelog.care, but changing authDomain to the custom
  // domain caused redirect_uri_mismatch in production. Do not change this
  // unless the Google/Firebase OAuth redirect configuration is updated too.
  authDomain: "lifelog-tracker.firebaseapp.com",
  projectId: "lifelog-tracker",
  storageBucket: "lifelog-tracker.firebasestorage.app",
  messagingSenderId: "268540727297",
  appId: "1:268540727297:web:aea31f933006c0279a7000",
  measurementId: "G-TQF8E426RG",
};

const testFirebaseConfig = {
  apiKey: "AIzaSyAU-HOueBWtQckWyl9sidsPtc6OdFM5XOQ",
  authDomain: "lifelog-test-aa7d6.firebaseapp.com",
  projectId: "lifelog-test-aa7d6",
  storageBucket: "lifelog-test-aa7d6.firebasestorage.app",
  messagingSenderId: "295687554521",
  appId: "1:295687554521:web:ebfde9a575ee048fee85c8",
  measurementId: "",
};

const getFirebaseConfigFromEnv = () => {
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };

  const hasAnyEnvOverride = REQUIRED_FIREBASE_FIELDS.some(
    (field) => Boolean(firebaseConfig[field]),
  );

  if (!hasAnyEnvOverride) {
    return null;
  }

  const missingFields = REQUIRED_FIREBASE_FIELDS.filter(
    (field) => !firebaseConfig[field],
  );

  if (missingFields.length > 0) {
    throw new Error(
      `Missing Firebase env config fields: ${missingFields.join(", ")}`,
    );
  }

  return firebaseConfig;
};

const appEnv = process.env.REACT_APP_APP_ENV || "test";
const envFirebaseConfig = getFirebaseConfigFromEnv();

const firebaseConfig =
  envFirebaseConfig ||
  (appEnv === "test" ? testFirebaseConfig : productionFirebaseConfig);

if (typeof window !== "undefined") {
  window.__LIFELOG_APP_ENV__ = appEnv;
}

const shouldLogFirebaseProject =
  typeof window !== "undefined" &&
  process.env.NODE_ENV !== "production";

if (shouldLogFirebaseProject) {
  // Helpful while the repo is transitioning from the old CaptureEz layout.
  // We want it to be obvious which Firebase project the app is using.
  console.info(
    `[firebase] appEnv=${appEnv} Firebase project=${firebaseConfig.projectId}`,
  );
}

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Firestore watch streams can trip browser-specific WebChannel issues in dev.
// Force long polling to keep realtime listeners stable across local/test/prod.
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
  experimentalLongPollingOptions: {
    timeoutSeconds: 25,
  },
});
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");
export const firebaseAppEnv = appEnv;
export const firebaseMeasurementId = firebaseConfig.measurementId || "";

let messagingSupportPromise = null;
let messagingInstance = null;

export const getFirebaseMessaging = async () => {
  if (typeof window === "undefined") {
    return null;
  }

  if (!messagingSupportPromise) {
    messagingSupportPromise = isMessagingSupported().catch(() => false);
  }

  const isSupported = await messagingSupportPromise;
  if (!isSupported) {
    return null;
  }

  if (!messagingInstance) {
    messagingInstance = getMessaging(app);
  }

  return messagingInstance;
};

if (isE2EMockEnabled()) {
  auth.onAuthStateChanged = (callback) => subscribeE2EAuth(callback);
}

export { app };
